import { StoreImageEnum, defaultLinkValues, getCommonItem } from '@systemeio/digital-store-shared'
import { FormInput, FormInputUrl, LanguageCodeEnum, toast } from '@systemeio/ui'
import ItemActions from 'modules/stores/store/components/item-actions'
import ItemSteps from 'modules/stores/store/components/item-steps'
import StoreImageSelector from 'modules/stores/store/components/store-image-selector'
import { STORE_ITEM_CLASS_NAME } from 'modules/stores/store/constants/store-item-class-name'
import { defaultErrors } from 'modules/stores/store/content/link/constants/default-link-errors'
import { useLinkActions } from 'modules/stores/store/content/link/hooks/use-link-actions'
import {
  LinkItemEditType,
  LinkItemErrorsType,
} from 'modules/stores/store/content/link/types/link-interface'
import { useStorePreviewContext } from 'modules/stores/store/context/store-preview-context'
import { useStore } from 'modules/stores/store/hooks/use-store'
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query'
import { getComplexItemUpdateData } from 'modules/stores/store/utils/get-complex-item-update-data'
import { StoreId } from 'modules/stores/types/stores-interface'
import { FC, useEffect, useState } from 'react'
import FormTextarea from 'shared/components/form-textarea'
import { ConfirmationModalBeforeExit } from 'shared/confirmation-modal-before-exit/component'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { deepCompare } from 'shared/utils/deepCompare'
import { isValidUrl } from 'shared/utils/is-valid-url'
import { handle422Error } from 'shared/utils/parse-422-errors'
import { twMerge } from 'tailwind-merge'
import { useStoreEditItemContext } from '../../context/store-item-edit-context'

interface LinkEditProps {
  storeId: StoreId
  link?: LinkItemEditType
  languageCode: LanguageCodeEnum
}

const LinkEdit: FC<LinkEditProps> = ({ storeId, link, languageCode }) => {
  const { t } = useLocoTranslation()

  const { redirectToMainPage } = useStoreQuery()

  const { mutate } = useStore(storeId)

  const defaultValues: LinkItemEditType = {
    ...defaultLinkValues,
    draft: false,
    title: t('dashboard.store.link.title_default', { lng: languageCode }),
    subTitle: t('dashboard.store.link.subtitle_default', { lng: languageCode }),
    buttonTitle: t('dashboard.store.link.button_default', { lng: languageCode }),
  }

  const [tempLink, setTempLink] = useState<LinkItemEditType>(link || defaultValues)

  const [isDataSaved, setIsDataSaved] = useState<boolean>(true)

  const { errors: initialErrors, setErrors: setEditItemErrors } = useStoreEditItemContext()
  const [errors, setErrors] = useState<LinkItemErrorsType>({
    ...defaultErrors,
    ...initialErrors,
  })

  useEffect(() => {
    return () => {
      setEditItemErrors({})
    }
  }, [])

  const [isFetchingPublish, setIsFetchingPublish] = useState(false)
  const [isFetchingDraft, setIsFetchingDraft] = useState(false)

  const { createLink, updateLink } = useLinkActions()

  const { setPreview, setSingleItemMode } = useStorePreviewContext()

  useEffect(() => {
    setPreview([getCommonItem(tempLink)])
    setSingleItemMode(true)
  }, [setPreview, setSingleItemMode, tempLink])

  const onSave = async (link: LinkItemEditType) => {
    try {
      setErrors(defaultErrors)

      if (link.id) {
        const item = await updateLink(link.id, getComplexItemUpdateData(link))
        await mutate(
          data =>
            data && {
              ...data,
              items: data.items.map(i => (i.itemId === item.itemId ? item : i)),
            },
          false,
        )
      } else {
        const item = await createLink(storeId, getComplexItemUpdateData(link))

        await mutate(data => data && { ...data, items: [...data.items, item] }, false)
      }

      toast.success(t('global.changes_saved'))

      await redirectToMainPage()
    } catch (e) {
      handle422Error(e, setErrors)
    } finally {
      setIsFetchingPublish(false)
      setIsFetchingDraft(false)
    }
  }

  const onPublish = async () => {
    const link: LinkItemEditType = { ...tempLink, draft: false }
    setIsFetchingPublish(true)
    setTempLink(link)
    await onSave(link)
  }

  const onSaveDraft = async () => {
    const link: LinkItemEditType = { ...tempLink, draft: true }
    setIsFetchingDraft(true)
    setTempLink(link)
    await onSave(link)
  }

  const validateUrl = (url: string) => {
    if (url && !isValidUrl(url)) {
      setErrors(prev => ({ ...prev, url: t('dashboard.settings.affiliate_program.invalid_url') }))
    } else {
      setErrors(prev => ({ ...prev, url: '' }))
    }
  }

  const isReady =
    !!tempLink.title &&
    !!tempLink.url &&
    !errors.url &&
    !!tempLink.subTitle &&
    !!tempLink.buttonTitle

  useEffect(() => {
    if (link) {
      setIsDataSaved(deepCompare(tempLink, link))
    } else {
      setIsDataSaved(deepCompare(tempLink, defaultValues))
    }
  }, [tempLink])

  return (
    <div className={twMerge(STORE_ITEM_CLASS_NAME, 'flex flex-col gap-5')}>
      <ItemSteps
        steps={[
          {
            key: 'image',
            title: t('dashboard.store.image.select_image'),
            element: (
              <StoreImageSelector
                imageType={StoreImageEnum.link_thumbnail}
                dataFile={tempLink.thumbnail}
                label={t('dashboard.store.image.thumbnail')}
                onChange={dataFile =>
                  setTempLink(prev => ({
                    ...prev,
                    thumbnail: dataFile,
                  }))
                }
              />
            ),
          },
          {
            key: 'text',
            title: t('dashboard.store.item.add_text'),
            element: (
              <div className={'flex flex-col gap-5'}>
                <div className={'grid grid-cols-1 gap-5 sm:grid-cols-2'}>
                  <FormInput
                    required
                    value={tempLink.title}
                    label={t('dashboard.store.item.title')}
                    onChange={({ target: { value } }) =>
                      setTempLink(prev => ({ ...prev, title: value }))
                    }
                    error={errors.title}
                  />
                  <FormInput
                    required
                    value={tempLink.buttonTitle}
                    label={t('dashboard.store.item.button')}
                    onChange={({ target: { value } }) =>
                      setTempLink(prev => ({ ...prev, buttonTitle: value }))
                    }
                    error={errors.buttonTitle}
                  />
                </div>
                <FormTextarea
                  required
                  value={tempLink.subTitle}
                  label={t('dashboard.store.item.subtitle')}
                  onChange={({ target: { value } }) =>
                    setTempLink(prev => ({ ...prev, subTitle: value }))
                  }
                  error={errors.subTitle}
                />
              </div>
            ),
          },
          {
            key: 'url',
            title: t('dashboard.store.item.paste_url'),
            element: (
              <FormInputUrl
                withCopyButton
                onBlur={() => validateUrl(tempLink.url)}
                value={tempLink.url}
                label={t('global.url')}
                onChange={value => setTempLink(prev => ({ ...prev, url: value }))}
                error={errors.url}
              />
            ),
          },
        ]}
      />
      <ItemActions
        publish={{ disabled: !isReady, onClick: onPublish, isFetching: isFetchingPublish }}
        saveDraft={{ onClick: onSaveDraft, isFetching: isFetchingDraft }}
      />
      <ConfirmationModalBeforeExit
        confirmationText={t('dashboard.store.confirmation_message_before_exit')}
        hideModal={isDataSaved || isFetchingDraft || isFetchingPublish}
      />
    </div>
  )
}

export default LinkEdit
